import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SiteHeader from '../../components/SiteHeader/SiteHeader';
import MasterDegreeView from '../../components/MasterDegreeView/MasterDegreeView';
import SpecialityView from '../../components/SpecialityView/SpecialityView';
import BachilleratoView from '../../components/BachilleratoView/BachilleratoView';
import SyllabusDownloadButton from '../../components/SyllabusDownloadButton/SyllabusDownloadButton';
import styles from './AdmisionesPage.module.css';

const API = process.env.REACT_APP_API_URL || '/api';


function AdmisionesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preferType = searchParams.get('type') || 'degree';
  const preferId   = searchParams.get('id')   || null;

  const [detail, setDetail] = useState(null);
  const [loadedType, setLoadedType] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setDetail(null);

    const fetchByType = (type, id) => {
      const endpoint =
        type === 'master'       ? `${API}/master-degrees/${id}` :
        type === 'speciality'   ? `${API}/specialities/${id}`   :
        type === 'tsu'          ? `${API}/tsu/${id}`            :
        type === 'bachillerato' ? `${API}/bachillerato/${id}`   :
                                  `${API}/degrees/${id}`;
      return fetch(endpoint).then((r) => r.json()).then(({ data }) => data);
    };

    const fetchFirstOfType = (type) => {
      const listEndpoint =
        type === 'master'       ? `${API}/master-degrees/list` :
        type === 'speciality'   ? `${API}/specialities/list`   :
        type === 'tsu'          ? `${API}/tsu/list`            :
        type === 'bachillerato' ? `${API}/bachillerato/list`   :
                                  `${API}/degrees/list`;
      return fetch(listEndpoint)
        .then((r) => r.json())
        .then(({ data }) => {
          if (!data?.length) return null;
          return fetchByType(type, data[0]._id);
        });
    };

    const load = preferId
      ? fetchByType(preferType, preferId)
      : fetchFirstOfType(preferType);

    load
      .then((data) => {
        setDetail(data);
        setLoadedType(preferType);
      })
      .finally(() => setLoading(false));
  }, [preferType, preferId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.page}>
      <SiteHeader />

      <main className={styles.main}>
        {loading || !detail || loadedType !== preferType ? (
          <div className={styles.skeleton}>
            <div className={styles.skeletonBanner} />
            <div className={styles.skeletonContent}>
              <div className={styles.skeletonLine} style={{ width: '60%' }} />
              <div className={styles.skeletonLine} style={{ width: '90%' }} />
              <div className={styles.skeletonLine} style={{ width: '75%' }} />
            </div>
          </div>
        ) : preferType === 'master' ? (
          <MasterDegreeView masterDegree={detail} />
        ) : preferType === 'speciality' ? (
          <SpecialityView speciality={detail} />
        ) : preferType === 'bachillerato' ? (
          <BachilleratoView bachillerato={detail} />
        ) : (
          <>
            {/* Banner */}
            <div className={styles.banner}>
              {detail.bannerImageS3 ? (
                <img src={detail.bannerImageS3} alt={detail.name} className={styles.bannerImg} />
              ) : (
                <div className={styles.bannerPlaceholder} />
              )}
              <div className={styles.bannerOverlay}>
                <div className={styles.bannerLeft}>
                  <h1 className={styles.bannerTitle}>{detail.name}</h1>
                  {detail.generalInfo && (
                    <div className={styles.heroGrid}>
                      {detail.generalInfo.duration && (
                        <div className={styles.heroItem}>
                          <span className={styles.heroLabel}>Duración</span>
                          <span className={styles.heroValue}>{detail.generalInfo.duration}</span>
                        </div>
                      )}
                      {detail.generalInfo.schedule && (
                        <div className={styles.heroItem}>
                          <span className={styles.heroLabel}>Horarios</span>
                          <span className={styles.heroValue}>{detail.generalInfo.schedule}</span>
                        </div>
                      )}
                      {detail.generalInfo.subjects && (
                        <div className={styles.heroItem}>
                          <span className={styles.heroLabel}>Materias</span>
                          <span className={styles.heroValue}>{detail.generalInfo.subjects}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className={styles.bannerRight}>
                  <button
                    type="button"
                    className={styles.bannerCta}
                    onClick={() => navigate(`/inscripcion?careerId=${detail._id}`)}
                  >
                    Inscríbete ahora
                  </button>
                </div>
              </div>
            </div>

            {/* Two-column content */}
            <div className={styles.content}>
              {/* ── Left column ── */}
              <div className={styles.leftCol}>
                {detail.tagline && <p className={styles.tagline}>{detail.tagline}</p>}
                {detail.description && <p className={styles.description}>{detail.description}</p>}

                {/* ¿Qué vas a aprender? | Campo laboral + Costos */}
                <div className={styles.learnCareerGrid}>
                  {/* Columna izquierda — ¿Qué vas a aprender? */}
                  {detail.whatYouWillLearn?.length > 0 && (
                    <section className={styles.section}>
                      <h2 className={styles.sectionTitle}>¿Qué vas a aprender?</h2>
                      <div className={styles.learnGrid}>
                        {detail.whatYouWillLearn.map((block, i) => (
                          <div key={i} className={styles.learnBlock}>
                            <h3 className={styles.learnCategory}>{block.category}</h3>
                            <ul className={styles.subjectList}>
                              {block.subjects.map((s, j) => (
                                <li key={j}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Columna derecha — Campo laboral + Costos */}
                  <div className={styles.learnCareerRight}>
                    {detail.careerField?.length > 0 && (
                      <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>Campo laboral</h2>
                        <ul className={styles.bulletList}>
                          {detail.careerField.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </section>
                    )}

                    <SyllabusDownloadButton url={detail.syllabusUrl} />

                  </div>
                </div>

                {detail.graduateProfile?.length > 0 && (
                  <section className={styles.profileCapsule}>
                    <div className={styles.profileLeft}>
                      <h2 className={styles.sectionTitle}>Perfil del egresado</h2>
                      <ul className={styles.bulletList}>
                        {detail.graduateProfile.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <img src="/egresado.png" alt="" className={styles.degreeImg01} aria-hidden="true" />
                  </section>
                )}
              </div>

              {/* ── Right column ── */}
              <div className={styles.rightCol}>
                {detail.generalInfo && (
                  <div className={styles.infoCard}>
                    <div className={styles.infoRow}>
                      <span className={styles.infoIcon}>📅</span>
                      <div>
                        <p className={styles.infoLabel}>Duración</p>
                        <p className={styles.infoValue}>{detail.generalInfo.duration}</p>
                      </div>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoIcon}>🕐</span>
                      <div>
                        <p className={styles.infoLabel}>Horarios</p>
                        <p className={styles.infoValue}>{detail.generalInfo.schedule}</p>
                      </div>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoIcon}>📚</span>
                      <div>
                        <p className={styles.infoLabel}>Materias</p>
                        <p className={styles.infoValue}>{detail.generalInfo.subjects}</p>
                      </div>
                    </div>
                    {detail.generalInfo.graduation?.length > 0 && (
                      <div className={styles.infoRow}>
                        <span className={styles.infoIcon}>🎓</span>
                        <div>
                          <p className={styles.infoLabel}>Titulación</p>
                          <ol className={styles.graduationList}>
                            {detail.generalInfo.graduation.map((g, i) => (
                              <li key={i}>{g}</li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.ctaBtn}
                      onClick={() => navigate(`/inscripcion?careerId=${detail._id}`)}
                    >
                      Comenzar tu inscripción ahora
                    </button>
                  </div>
                )}

              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default AdmisionesPage;
